import { ActionContext, ActionTree, GetterTree, MutationTree } from "vuex";
import {
  KoalaLoginResponse,
  KoalaTokenResponse,
  KokattoTokenResponse,
  ProductCatalogTokenResponse,
} from "@/serviceClients/responses/KoalaLoginResponse";
import router from "@/router";
import { RefreshableRequestHelper } from "@/helpers/RefreshableRequestHelper";
import { KoalaAccountServiceClient } from "@/serviceClients/KoalaAccountServiceClient";
import { KokattoAuthServiceClient } from "@/serviceClients/KokattoAuthServiceClient";
import { KoalaPlusAccountActivityRequest } from "@/serviceClients/requests/KoalaPlusAccountActivityRequest";
import { KoalaPlusAccountActivityServiceClient } from "@/serviceClients/KoalaPlusAccountActivityServiceClient";
import { getKoalaUserDetail, getUserDetailFromJwt } from "@/utils/jwtUtil";
import {
  KokattoGetCustomerServiceDetailResponse,
  KokattoGetSupervisorDetailsResponse,
} from "@/serviceClients/responses/KokattoGetCustomerServiceDetailResponse";
import {
  isKokattoErrorResponse,
  KokattoErrorResponse,
} from "@/serviceClients/responses/KokattoErrorResponse";
import { KokattoCustomerServiceServiceClient } from "@/serviceClients/KokattoCustomerServiceServiceClient";
import { CentralizedIdentityServiceClient } from "@/serviceClients/CentralizedIdentityServiceClient";
import { KokattoLoggingServiceClient } from "@/serviceClients/KokattoLoggingServiceClient";
import { CustomerServiceModel } from "@/models/CsAgentManagementModel";
import defineAbilityFor from "@/brand/current/ability";
import { Ability } from "@casl/ability";
import { KokattoGetCustomerServiceDetailsRequest } from "@/serviceClients/requests/KokattoGetCustomerServiceDetailsRequest";
import { ClientSessionKeyManager } from "@/helpers/ClientSessionKeyManager";

type AgentStatusState = {
  id: string;
  name: string;
  description?: string;
  icon?: string;
};

type State = {
  isLoggedIn: boolean;
  clientId: string;
  koalaToken: {
    accessToken: string;
    refreshToken: string;
  };
  productCatalogToken: {
    accessToken: string;
    accessTokenExptime: number;
    refreshToken: string;
    refreshTokenExptime: number;
  };
  kokattoToken: {
    token: string;
    tokenExpireAt: number;
    refreshToken: string;
    refreshTokenExpireAt: number;
    role: string;
    features: number;
    clientId: string;
    daysTillExpiration: string;
    featuresAccessName: string;
    userIsApiUser?: boolean;
  };
  loginEmail: string;
  sharedLoginEmail: string;
  finishedTutorials: string[];
  agentStatus: AgentStatusState;
  lastAgentStatus: AgentStatusState;
  enableMultiClient: boolean;
  customerService: CustomerServiceModel | null;
  csRole: string; // CS, CS-SPV, SPV
};

const state: State = {
  isLoggedIn: false,
  clientId: "",
  koalaToken: {
    accessToken: "",
    refreshToken: "",
  },
  productCatalogToken: {
    accessToken: "",
    accessTokenExptime: 0,
    refreshToken: "",
    refreshTokenExptime: 0,
  },
  kokattoToken: {
    token: "",
    tokenExpireAt: 0,
    refreshToken: "",
    refreshTokenExpireAt: 0,
    role: "",
    features: 0,
    clientId: "",
    daysTillExpiration: "",
    featuresAccessName: "",
  },
  loginEmail: "",
  sharedLoginEmail: "",
  finishedTutorials: [],
  agentStatus: {
    id: "",
    name: "",
    description: "",
    icon: "",
  },
  lastAgentStatus: {
    id: "",
    name: "",
  },
  enableMultiClient: false,
  customerService: null,
  csRole: "", // CS, CS-SPV, SPV
};

const getters: GetterTree<State, any> = {
  getIsLoggedIn: (state) => state.isLoggedIn,
  getKoalaTokenAccess: (state) => state.koalaToken.accessToken,
  getKoalaTokenRefresh: (state) => state.koalaToken.refreshToken,
  getProductCatalogTokenAccess: (state) =>
    state.productCatalogToken.accessToken,
  getProductCatalogTokenRefresh: (state) =>
    state.productCatalogToken.refreshToken,
  getKokattoTokenAccess: (state) => state.kokattoToken.token,
  getKokattoTokenRefresh: (state) => state.kokattoToken.refreshToken,
  getClientId: (state) => state.clientId,
  // getAccountRole: (state) => state.kokattoToken.role,
  getLoginEmail: (state) => state.loginEmail,
  getSharedLoginEmail: (state) => state.sharedLoginEmail,
  getFinishedCoachmark: (state) => state.finishedTutorials,
  getAccountEmailFromAccount: (state, getters) => getters.getAccountEmail,
  getKokattoFeatureAccessName: (state) => state.kokattoToken.featuresAccessName,
  getAccountRegion: (state) =>
    getKoalaUserDetail(state.koalaToken.accessToken).region || "",
  getAgentStatus: (state) => state.agentStatus,
  getLastAgentStatus: (state) => state.lastAgentStatus,
  getIsEnableMultiClient: (state) => state.enableMultiClient,
  getCustomerService: (state) => state.customerService,
  getCsRole: (state) => state.csRole,
  getViewFeatureAbility: (state, getters, rootStaate, rootGetters) => {
    const user = getUserDetailFromJwt(rootGetters.getKokattoTokenAccess);

    const ability = new Ability(
      defineAbilityFor({
        role: rootGetters.getAccountRole,
        accountCountry: rootGetters.getAccountCountry,
        featuresAccessPermissions: user.featuresAccessPermission,
        featuresPermissions: user.featuresPermission,
      })
    );
    return ability.can("READ", "INBOUND_CALL");
  },
};

const mutations: MutationTree<State> = {
  updateIsLoggedIn(state, isLoggedIn: boolean) {
    state.isLoggedIn = isLoggedIn ?? false;
  },
  updateClientId(state, clientId: string) {
    state.clientId = clientId;
  },
  updateKoalaToken(state, response: KoalaTokenResponse) {
    state.koalaToken = response;
  },
  updateProductCatalogToken(state, response: ProductCatalogTokenResponse) {
    state.productCatalogToken = response;
  },
  updateKokattoToken(state, response: KokattoTokenResponse) {
    state.kokattoToken = response;
  },
  updateLoginEmail(state, email: string) {
    state.loginEmail = email;
  },
  updateSharedLoginEmail(state, email: string) {
    state.sharedLoginEmail = email;
  },
  updateFinishedCoachmark: (state, listFinishedCoachmark: string[]) => {
    state.finishedTutorials = listFinishedCoachmark;
  },
  updateAgentStatus(state, agentStatus: AgentStatusState) {
    state.agentStatus = agentStatus;
  },
  updateLastAgentStatus(state, lastAgentStatus: AgentStatusState) {
    state.lastAgentStatus = lastAgentStatus;
  },
  updateMultiClient(state, enableMultiClient) {
    state.enableMultiClient = enableMultiClient;
  },
  updateCustomerService(state, customerService: CustomerServiceModel) {
    state.customerService = customerService;
  },
  updateCsRole(state, csRole: string) {
    state.csRole = csRole;
  },
};

const actions: ActionTree<State, any> = {
  async storeToken(context, koalaLoginResponse: KoalaLoginResponse) {
    const { koalaToken, productCatalogToken, kokattoToken, clientInfo } =
      koalaLoginResponse.data;
    context.commit("updateKoalaToken", koalaToken);
    context.commit("updateProductCatalogToken", productCatalogToken);
    context.commit("updateKokattoToken", kokattoToken);
    context.commit("updateIsLoggedIn", true);
    context.commit("updateMultiClient", clientInfo?.enableMultiClient ?? false);

    // Initialize encryption session after successful login if encryption is enabled
    const isHidePhoneNumbers =
      context.getters["CSPConfiguration/getHidePhoneNumbers"] ?? false;
    console.log("isHidePhoneNumbers", isHidePhoneNumbers);
    if (
      process.env.VUE_APP_ENABLE_ENCRYPTION === "true" &&
      isHidePhoneNumbers
    ) {
      try {
        const sessionManager = ClientSessionKeyManager.getInstance();
        await sessionManager.initializeSession(true); // Force new session
        console.log("Encryption session initialized after login");
      } catch (error) {
        console.error("Failed to initialize encryption session:", error);
      }
    }
  },
  storeClientId(context, koalaLoginResponse: KoalaLoginResponse) {
    const { clientId, role } = koalaLoginResponse.data;
    context.commit("updateClientId", clientId);
    context.commit("updateAccountRole", role);
  },
  // TODO: add send token to service worker
  storeTokenKokatto(context, koalaLoginResponse: KoalaLoginResponse) {
    const { kokattoToken } = koalaLoginResponse.data;
    context.commit("updateKokattoToken", kokattoToken);

    // updates service worker logging data
    navigator.serviceWorker.controller?.postMessage(
      JSON.stringify({
        shouldLog: process.env.VUE_APP_CLIENT_IDS_TO_LOG?.includes(
          context.getters.getClientId
        ),
        kokattoToken: context.getters.getKokattoTokenAccess,
        cisUrl:
          context.getters.getAccountRegion === "JKT"
            ? process.env.VUE_APP_BASEURL_CIS_AWS_JKT
            : process.env.VUE_APP_BASEURL_CIS_AWS,
      })
    );
  },
  storeTokenProductCatalog(context, koalaLoginResponse: KoalaLoginResponse) {
    const { productCatalogToken } = koalaLoginResponse.data;
    context.commit("updateProductCatalogToken", productCatalogToken);
  },
  storeTokenKoala(context, koalaLoginResponse: KoalaLoginResponse) {
    const { koalaToken } = koalaLoginResponse.data;
    context.commit("updateKoalaToken", koalaToken);
  },
  storeLoginEmail(context, email: string) {
    context.commit("updateLoginEmail", email);
    context.commit("updateSharedLoginEmail", email);
  },
  clearToken(context) {
    context.commit("updateFcmToken", "");
    context.commit("updateKoalaToken", "");
    context.commit("updateKokattoToken", "");
    context.commit("updateProductCatalogToken", "");
    context.commit("updateIsLoggedIn", false);
    context.commit("updateLoginEmail", "");
    context.commit("updateSharedLoginEmail", "");
    context.commit("updateClientId", "");

    navigator.serviceWorker.controller?.postMessage(
      JSON.stringify({
        kokattoToken: null,
        shouldLog: false,
      })
    );
  },
  setFinishedCoachmark(context, listFinishedCoachmark: string[]) {
    context.commit("updateFinishedCoachmark", listFinishedCoachmark);
  },
  clearStoreLogout({ dispatch, commit }) {
    dispatch("clearToken");
    dispatch("clearAccount");
    dispatch("clearConfiguration");
    dispatch("clearState");
    dispatch("clearMainOverlay");
    dispatch("clearAddress");
    dispatch("clearShowTemplateSubmiDisclaimer");
    dispatch("clearWSState");
    dispatch("clearDownloadedAttachments");
    dispatch("CustomerServicePlayMode/clearPlayModeData");
    commit("TicketChatListCustomHeader/resetStateToDefault");
    commit("ActiveCallController/resetTemporaryState");
    dispatch("clearCustomerServiceData");
  },
  async submitLog(context, { payload, isError, type = "AUTH_LOG" }) {
    const token = context.getters.getKokattoTokenAccess;
    if (!token) return;

    const serviceClient = new KokattoLoggingServiceClient({
      token: token,
    });

    try {
      const trackerData = {
        ...payload,
        createdAt: new Date().toISOString(),
      };

      const request = {
        event: JSON.stringify(trackerData),
        result: isError ? "error" : "success",
        type: type,
      };

      serviceClient.postLog(request);
    } catch (error) {
      console.error(error);
    }
  },
  async logoutAccount({ dispatch, getters }) {
    // Clear encryption session
    const isHidePhoneNumbers =
      getters["CSPConfiguration/getHidePhoneNumbers"] ?? false;
    if (
      process.env.VUE_APP_ENABLE_ENCRYPTION === "true" &&
      isHidePhoneNumbers
    ) {
      const sessionManager = ClientSessionKeyManager.getInstance();
      sessionManager.clearSession();
    }

    await dispatch("ActiveCallController/disconnectAllActiveCalls");
    await dispatch("revokeAccountKokatto");
    await dispatch("revokeAccountActivity");
    await dispatch("logoutAndRevokeTokenKokatto");
    dispatch("clearStoreLogout");
    router.push("/login");
  },
  async revokeAccountKokatto() {
    return RefreshableRequestHelper.requestKokatto<KoalaAccountServiceClient>(
      () => {
        const serviceClient = new KokattoAuthServiceClient();
        try {
          return serviceClient.updateStatusCSP();
        } catch (err) {
          console.log("Failed revoke account kokatto");
          return err as any;
        }
      }
    );
  },
  async revokeAccountActivity({ getters }) {
    const req: KoalaPlusAccountActivityRequest = {
      clientId: getters.getClientId,
      email: getters.getAccountEmailFromAccount,
    };
    return RefreshableRequestHelper.requestKoala<KoalaAccountServiceClient>(
      () => {
        // eslint-disable-next-line prefer-const
        let serviceClient = new KoalaPlusAccountActivityServiceClient();
        try {
          return serviceClient.deleteAccountActivity(req);
        } catch (err) {
          console.log("Failed revoke account activity");
          return err as any;
        }
      }
    );
  },
  /**
   * Will Post a logout request to CIS
   * Use to invalidate token & post blacklisted token to redis
   * @param param0
   * @returns
   */
  async logoutAndRevokeTokenKokatto({ getters }) {
    return RefreshableRequestHelper.requestKokatto<CentralizedIdentityServiceClient>(
      () => {
        const serviceClient = new CentralizedIdentityServiceClient();
        try {
          return serviceClient.logout(getters.getKokattoTokenAccess);
        } catch (err) {
          console.log("Failed logout account", err);
          return err as any;
        }
      }
    );
  },
  // adding agent status detail to store
  setAgentStatus(context, agentStatus: AgentStatusState) {
    context.commit("updateAgentStatus", agentStatus);
  },
  async getCustomerServiceDetails(
    context: ActionContext<State, any>
  ): Promise<void> {
    const { commit, dispatch, getters } = context;
    try {
      const request: KokattoGetCustomerServiceDetailsRequest = {};
      if (getters.getViewFeatureAbility) {
        request.excludedChannel = true;
      }
      const response:
        | KokattoGetCustomerServiceDetailResponse
        | KokattoErrorResponse = await RefreshableRequestHelper.requestKokatto<KokattoGetCustomerServiceDetailResponse>(
        () => {
          const kokattoCustomerServiceServiceClient =
            new KokattoCustomerServiceServiceClient();

          return kokattoCustomerServiceServiceClient.getCustomerServiceDetails(
            request
          );
        }
      );
      dispatch("CustomerServicePlayMode/updateAgentPlayModeData", response);

      let isActiveCustomerService = false;
      if (!isKokattoErrorResponse(response)) {
        const { customerService } =
          response as KokattoGetCustomerServiceDetailResponse;

        // Default status if agent is first logs in
        const DEFAULT_STATUS = {
          id: "online",
          name: "ONLINE",
        };

        // Retrieve agent last status before last logout
        const {
          lastCsAvailabilityId = DEFAULT_STATUS.id,
          lastStatusDetail = DEFAULT_STATUS.name,
        } = customerService;

        // Save agent last status
        commit("updateLastAgentStatus", {
          id: lastCsAvailabilityId,
          name: lastStatusDetail,
        });

        // Retrieve agent current status
        const { csAvailabilityId, statusDetail, statusIcon } = customerService;
        // Save agent current status
        commit("updateAgentStatus", {
          id: csAvailabilityId,
          name: statusDetail,
          icon: statusIcon,
        });
        // set customer service to store
        commit("updateCustomerService", customerService);
        if (!customerService.deactivated) {
          isActiveCustomerService = true;
        }
      } else {
        commit("updateAgentStatus", {
          id: "",
          name: "",
          description: "",
          icon: "",
        });
        commit("updateCustomerService", null);
      }

      // get supervisor details to check if agent is a supervisor
      const supervisorDetails:
        | KokattoGetSupervisorDetailsResponse
        | KokattoErrorResponse = await RefreshableRequestHelper.requestKokatto<KokattoGetSupervisorDetailsResponse>(
        () => {
          const kokattoCustomerServiceServiceClient =
            new KokattoCustomerServiceServiceClient();

          return kokattoCustomerServiceServiceClient.getSupervisorDetails();
        }
      );
      // Set chat role based on response
      if (
        !isKokattoErrorResponse(supervisorDetails) &&
        isActiveCustomerService
      ) {
        dispatch("setCsRole", "CS-SPV");
      } else if (!isKokattoErrorResponse(supervisorDetails)) {
        dispatch("setCsRole", "SPV");
      } else if (isActiveCustomerService) {
        dispatch("setCsRole", "CS");
      } else {
        dispatch("setCsRole", "");
      }
    } catch (error) {
      console.log("Auth ~ getCustomerServiceDetails ~ error:", error);
      throw error;
    }
  },
  setCsRole(context, csRole: string) {
    context.commit("updateCsRole", csRole);
  },
  clearCustomerServiceData(context) {
    context.commit("updateCustomerService", null);
    context.commit("updateCsRole", "");
    context.commit("updateAgentStatus", {
      id: "",
      name: "",
      description: "",
      icon: "",
    });
    context.commit("updateLastAgentStatus", {
      id: "",
      name: "",
      description: "",
      icon: "",
    });
  },
};

const Auth = { state, getters, mutations, actions };

export default Auth;
